import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, EyeIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../styles/quill-editor.css';
import { toast } from 'react-hot-toast';

// Custom styles for Quill editor in modal
const quillModalStyles = `
  .email-template-modal .ql-container {
    flex: 1;
    overflow: hidden;
  }
  .email-template-modal .ql-editor {
    min-height: 200px;
    max-height: none;
    overflow-y: auto;
  }
  .email-template-modal .ql-toolbar {
    border-top: 1px solid #e5e7eb;
    border-left: 1px solid #e5e7eb;
    border-right: 1px solid #e5e7eb;
  }
  .email-template-modal .ql-container {
    border-bottom: 1px solid #e5e7eb;
    border-left: 1px solid #e5e7eb;
    border-right: 1px solid #e5e7eb;
  }
`;

const EmailTemplateModal = ({ 
  isOpen, 
  onClose, 
  template = null, 
  onSave, 
  templateTypes = [],
  onPreview 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    subject: '',
    content: '',
    description: '',
    isActive: true,
    isDefault: false,
    variables: []
  });

  const [customVariables, setCustomVariables] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('user');
  const quillRef = useRef(null);

  // Comprehensive variable definitions
  const allAvailableVariables = {
    user: [
      { name: '{{userName}}', description: 'Full name of the user', example: 'John Doe', category: 'User' },
      { name: '{{userEmail}}', description: 'Email address of the user', example: 'john@example.com', category: 'User' },
      { name: '{{userFirstName}}', description: 'First name of the user', example: 'John', category: 'User' },
      { name: '{{userLastName}}', description: 'Last name of the user', example: 'Doe', category: 'User' },
      { name: '{{userPhone}}', description: 'Phone number of the user', example: '+1234567890', category: 'User' }
    ],
    form: [
      { name: '{{formName}}', description: 'Name of the form', example: 'Business Registration Form', category: 'Form' },
      { name: '{{formId}}', description: 'Unique form identifier', example: 'FORM-2024-001', category: 'Form' },
      { name: '{{submissionDate}}', description: 'Date when form was submitted', example: '2024-01-15', category: 'Form' },
      { name: '{{submissionId}}', description: 'Unique submission identifier', example: 'SUB-2024-001', category: 'Form' },
      { name: '{{formStatus}}', description: 'Current status of the form', example: 'In Progress', category: 'Form' }
    ],
    client: [
      { name: '{{clientName}}', description: 'Name of the client', example: 'ABC Company Ltd', category: 'Client' },
      { name: '{{clientEmail}}', description: 'Email address of the client', example: 'contact@abc.com', category: 'Client' },
      { name: '{{clientPhone}}', description: 'Phone number of the client', example: '+1234567890', category: 'Client' },
      { name: '{{clientAddress}}', description: 'Address of the client', example: '123 Business St, City', category: 'Client' }
    ],
    product: [
      { name: '{{productName}}', description: 'Name of the product', example: 'Business License', category: 'Product' },
      { name: '{{productDescription}}', description: 'Description of the product', example: 'Annual business license', category: 'Product' },
      { name: '{{totalFees}}', description: 'Total fees for the product', example: '$1,000.00', category: 'Product' },
      { name: '{{governmentFees}}', description: 'Government fees amount', example: '$500.00', category: 'Product' },
      { name: '{{professionalFees}}', description: 'Professional fees amount', example: '$300.00', category: 'Product' },
      { name: '{{otherFees}}', description: 'Other fees amount', example: '$200.00', category: 'Product' }
    ],
    invoice: [
      { name: '{{invoiceNumber}}', description: 'Invoice number', example: 'INV-2024-001', category: 'Invoice' },
      { name: '{{invoiceDate}}', description: 'Date of invoice', example: '2024-01-15', category: 'Invoice' },
      { name: '{{dueDate}}', description: 'Due date for payment', example: '2024-02-15', category: 'Invoice' },
      { name: '{{totalAmount}}', description: 'Total invoice amount', example: '$1,000.00', category: 'Invoice' },
      { name: '{{paymentLink}}', description: 'Link to payment page', example: 'https://pay.example.com/inv-001', category: 'Invoice' }
    ],
    system: [
      { name: '{{companyName}}', description: 'Your company name', example: 'Data Manager Pro', category: 'System' },
      { name: '{{companyEmail}}', description: 'Your company email', example: 'support@datamanager.com', category: 'System' },
      { name: '{{companyPhone}}', description: 'Your company phone', example: '+1234567890', category: 'System' },
      { name: '{{companyAddress}}', description: 'Your company address', example: '123 Tech St, City', category: 'System' },
      { name: '{{websiteUrl}}', description: 'Your website URL', example: 'https://datamanager.com', category: 'System' },
      { name: '{{loginUrl}}', description: 'Login page URL', example: 'https://datamanager.com/login', category: 'System' },
      { name: '{{supportEmail}}', description: 'Support email address', example: 'support@datamanager.com', category: 'System' }
    ],
    datetime: [
      { name: '{{currentDate}}', description: 'Current date', example: '2024-01-15', category: 'Date/Time' },
      { name: '{{currentTime}}', description: 'Current time', example: '14:30:00', category: 'Date/Time' },
      { name: '{{expiryDate}}', description: 'Expiry date', example: '2024-12-31', category: 'Date/Time' },
      { name: '{{deadlineDate}}', description: 'Deadline date', example: '2024-02-15', category: 'Date/Time' }
    ],
    verification: [
      { name: '{{verificationCode}}', description: 'Verification code', example: '123456', category: 'Verification' },
      { name: '{{verificationLink}}', description: 'Verification link', example: 'https://verify.example.com/code', category: 'Verification' },
      { name: '{{expiryTime}}', description: 'Expiry time for verification', example: '10 minutes', category: 'Verification' }
    ],
    iterator: [
      { name: '{{#each products}}', description: 'Start loop for products array', example: '{{#each products}}...{{/each}}', category: 'Iterator', isLoop: true },
      { name: '{{/each}}', description: 'End loop for products array', example: '{{#each products}}...{{/each}}', category: 'Iterator', isLoop: true },
      { name: '{{#each documents}}', description: 'Start loop for documents array', example: '{{#each documents}}...{{/each}}', category: 'Iterator', isLoop: true },
      { name: '{{#each contactPersons}}', description: 'Start loop for contact persons array', example: '{{#each contactPersons}}...{{/each}}', category: 'Iterator', isLoop: true },
      { name: '{{#each fees}}', description: 'Start loop for fees array', example: '{{#each fees}}...{{/each}}', category: 'Iterator', isLoop: true },
      { name: '{{#if condition}}', description: 'Start conditional block', example: '{{#if hasDocuments}}...{{/if}}', category: 'Iterator', isConditional: true },
      { name: '{{/if}}', description: 'End conditional block', example: '{{#if hasDocuments}}...{{/if}}', category: 'Iterator', isConditional: true },
      { name: '{{#unless condition}}', description: 'Start unless block (opposite of if)', example: '{{#unless isCompleted}}...{{/unless}}', category: 'Iterator', isConditional: true },
      { name: '{{/unless}}', description: 'End unless block', example: '{{#unless isCompleted}}...{{/unless}}', category: 'Iterator', isConditional: true }
    ],
    loopVariables: [
      { name: '{{@index}}', description: 'Current index in the loop (0-based)', example: '0, 1, 2...', category: 'Loop Variables', isLoopVar: true },
      { name: '{{@first}}', description: 'True if this is the first item in the loop', example: 'true/false', category: 'Loop Variables', isLoopVar: true },
      { name: '{{@last}}', description: 'True if this is the last item in the loop', example: 'true/false', category: 'Loop Variables', isLoopVar: true },
      { name: '{{@key}}', description: 'Key name for object iteration', example: 'propertyName', category: 'Loop Variables', isLoopVar: true },
      { name: '{{@root}}', description: 'Access root context from within a loop', example: '{{@root.clientName}}', category: 'Loop Variables', isLoopVar: true }
    ]
  };

  const categoryLabels = {
    user: '👤 User',
    form: '📋 Form',
    client: '🏢 Client',
    product: '📦 Product',
    invoice: '🧾 Invoice',
    system: '⚙️ System',
    datetime: '📅 Date/Time',
    verification: '🔐 Verification',
    iterator: '🔄 Iterators',
    loopVariables: '🔢 Loop Variables'
  };

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        type: template.type || '',
        subject: template.subject || '',
        content: template.content || '',
        description: template.description || '',
        isActive: template.isActive !== undefined ? template.isActive : true,
        isDefault: template.isDefault || false,
        variables: template.variables || []
      });
      setCustomVariables(template.customVariables || []);
    } else {
      setFormData({
        name: '',
        type: '',
        subject: '',
        content: '',
        description: '',
        isActive: true,
        isDefault: false,
        variables: []
      });
      setCustomVariables([]);
    }
  }, [template]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Function to insert variable at cursor position
  const insertVariable = (variableName) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      
      if (range) {
        // Handle special loop and conditional variables
        if (variableName.startsWith('{{#each') || variableName.startsWith('{{#if') || variableName.startsWith('{{#unless')) {
          // For loop starters, insert with proper formatting
          const insertText = variableName + '\n\n';
          quill.insertText(range.index, insertText);
          quill.setSelection(range.index + insertText.length);
        } else if (variableName === '{{/each}}' || variableName === '{{/if}}' || variableName === '{{/unless}}') {
          // For loop enders, insert with proper formatting
          const insertText = '\n' + variableName + '\n';   
          quill.insertText(range.index, insertText);
          quill.setSelection(range.index + insertText.length);
        } else if (variableName.startsWith('{{@')) {
          // For loop variables like @index, @first, etc.
          quill.insertText(range.index, variableName);
          quill.setSelection(range.index + variableName.length);
        } else {
          // For regular variables
          quill.insertText(range.index, variableName);
          quill.setSelection(range.index + variableName.length);
        }
        
        // Focus back to the editor
        quill.focus();
        
        toast.success(`Variable ${variableName} inserted`);
      } else {
        // If no selection, insert at the end
        const length = quill.getLength();
        
        if (variableName.startsWith('{{#each') || variableName.startsWith('{{#if') || variableName.startsWith('{{#unless')) {
          const insertText = variableName + '\n\n';
          quill.insertText(length - 1, insertText);
          quill.setSelection(length + insertText.length - 1);
        } else if (variableName === '{{/each}}' || variableName === '{{/if}}' || variableName === '{{/unless}}') {
          const insertText = '\n' + variableName + '\n';
          quill.insertText(length - 1, insertText);
          quill.setSelection(length + insertText.length - 1);
        } else {
          quill.insertText(length - 1, variableName);
          quill.setSelection(length + variableName.length - 1);
        }
        
        quill.focus();
        
        toast.success(`Variable ${variableName} inserted at the end`);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.type || !formData.subject || !formData.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    const submitData = {
      ...formData,
      variables: [...formData.variables, ...customVariables]
    };

    onSave(submitData);
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl h-[90vh] flex flex-col email-template-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {template ? 'Edit Email Template' : 'Create Email Template'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {template ? 'Update your email template' : 'Create a new email template'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Left Panel - Variables Tree */}
          <div className="w-1/3 border-r bg-gray-50 flex flex-col min-h-0">
            {/* Variables Header */}
            <div className="p-4 border-b bg-white flex-shrink-0">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Variables</h3>
              <p className="text-xs text-gray-600">Click any variable to insert it into the editor</p>
            </div>

            {/* Category Tabs */}
            <div className="flex border-b bg-white flex-shrink-0">
              <div className="flex-1 overflow-x-auto">
                <div className="flex">
                  {Object.keys(allAvailableVariables).map(category => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                        selectedCategory === category
                          ? 'border-blue-500 text-blue-600 bg-blue-50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {categoryLabels[category]}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Variables List */}
            <div className="flex-1 overflow-y-auto p-2 min-h-0">
              <div className="space-y-1">
                {allAvailableVariables[selectedCategory]?.map((variable, index) => (
                  <div
                    key={index}
                    onClick={() => insertVariable(variable.name)}
                    className="p-2 rounded cursor-pointer hover:bg-blue-100 transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {variable.name}
                        </div>
                        <div className="text-xs text-gray-600 truncate">
                          {variable.description}
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 group-hover:text-gray-600">
                        Click to insert
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Panel - Editor */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Form Fields */}
            <div className="p-6 border-b bg-white flex-shrink-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter template name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select template type</option>
                    {templateTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter email subject"
                  />
                </div>
              </div>
            </div>

            {/* Quill Editor */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="p-4 border-b bg-white flex-shrink-0">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Content *
                </label>
              </div>
              <div className="flex-1 min-h-0 relative" style={{ height: '400px' }}>
                <ReactQuill
                  ref={quillRef}
                  value={formData.content}
                  onChange={(value) => handleInputChange('content', value)}
                  modules={quillModules}
                  placeholder="Write your email content here..."
                  style={{
                    height: '100%'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Set as default</span>
            </label>
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {template ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateModal; 